import { nanoid } from "nanoid";
import type { EvaluationResult, MotiveDocument } from "../model/types";
import type { CadExportFormat, CadWorkerRequest, CadWorkerResponse, MeshQuality } from "./protocol";

type PendingRequest = {
  resolve: (value: CadWorkerResponse) => void;
  reject: (reason: Error) => void;
};

export class CadClient {
  private worker!: Worker;
  private pending = new Map<string, PendingRequest>();
  private readyPromise: Promise<void> = Promise.resolve();
  private failed = false;

  constructor() {
    this.startWorker();
  }

  private startWorker(): void {
    this.failed = false;
    this.worker = this.createWorker();
    this.readyPromise = this.initialise();
  }

  private createWorker(): Worker {
    const worker = new Worker(new URL("./cad.worker.ts", import.meta.url), { type: "module", name: "modelmotive-cad" });
    worker.onmessage = (event: MessageEvent<CadWorkerResponse>) => {
      const response = event.data;
      const pending = this.pending.get(response.requestId);
      if (!pending) return;
      this.pending.delete(response.requestId);
      if (response.type === "ERROR") pending.reject(new Error(response.message));
      else pending.resolve(response);
    };
    worker.onerror = (event) => {
      if (worker !== this.worker) return;
      const error = new Error(event.message || "CAD worker crashed");
      for (const pending of this.pending.values()) pending.reject(error);
      this.pending.clear();
      this.failed = true;
    };
    return worker;
  }

  private send(request: CadWorkerRequest): Promise<CadWorkerResponse> {
    return new Promise((resolve, reject) => {
      this.pending.set(request.requestId, { resolve, reject });
      this.worker.postMessage(request);
    });
  }

  private async initialise(): Promise<void> {
    const response = await this.send({ requestId: nanoid(), type: "INITIALISE" });
    if (response.type !== "READY") throw new Error("CAD worker failed to initialise");
  }

  private async ensureReady(): Promise<void> {
    if (this.failed) this.startWorker();
    try {
      await this.readyPromise;
    } catch (error) {
      if (!this.failed) throw error;
      this.startWorker();
      await this.readyPromise;
    }
  }

  async ready(): Promise<void> {
    return this.ensureReady();
  }

  async evaluate(document: MotiveDocument, revision: number, quality: MeshQuality = "preview"): Promise<EvaluationResult> {
    await this.ensureReady();
    const response = await this.send({ requestId: nanoid(), type: "EVALUATE", document, revision, quality });
    if (response.type !== "EVALUATION_RESULT") throw new Error("Unexpected CAD worker response");
    return response.result;
  }

  async export(document: MotiveDocument, revision: number, format: CadExportFormat): Promise<Blob> {
    await this.ensureReady();
    const response = await this.send({ requestId: nanoid(), type: "EXPORT", document, revision, format });
    if (response.type !== "EXPORT_RESULT") throw new Error("Unexpected CAD worker response");
    return new Blob([response.buffer], { type: response.mime });
  }

  restart(): void {
    this.worker.terminate();
    const error = new Error("CAD worker restarted");
    for (const pending of this.pending.values()) pending.reject(error);
    this.pending.clear();
    this.startWorker();
  }

  dispose(): void {
    this.worker.terminate();
    this.pending.clear();
  }
}

export const cadClient = new CadClient();
